import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Loader2, Award, XCircle, ArrowLeft } from "lucide-react";
import ReactMarkdown from "react-markdown";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * PublicProofView - Public page for viewing shared case studies
 * Accessed via /proof/:token (no auth required)
 */
export default function PublicProofView() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [proof, setProof] = useState(null);

  useEffect(() => {
    fetchProof();
  }, [token]);

  const fetchProof = async () => {
    try {
      const res = await axios.get(`${API}/proofs/public/${token}`);
      setProof(res.data);
    } catch (e) {
      if (e.response?.status === 404) {
        setError("This case study was not found or is not publicly available.");
      } else {
        setError("Unable to load case study. Please try again later.");
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-indigo-500 animate-spin mx-auto mb-4" />
          <p className="text-indigo-400">Loading case study...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4">
            <XCircle className="w-8 h-8 text-red-400" />
          </div>
          <h1 className="text-xl font-bold text-indigo-500 mb-2">Not Available</h1>
          <p className="text-indigo-400">{error}</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 mt-6 text-indigo-400 hover:text-indigo-400"
          >
            <ArrowLeft className="w-4 h-4" />
            Go to ClientKit
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4 sm:p-6 lg:p-8" data-testid="public-proof-view">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-indigo-500/20 flex items-center justify-center">
              <Award className="w-6 h-6 text-indigo-400" />
            </div>
            <div>
              <p className="text-xs text-slate-400 uppercase tracking-wide">Case Study</p>
              <h1 className="text-2xl sm:text-3xl font-bold text-indigo-500">{proof?.title}</h1>
            </div>
          </div>
          
          {proof?.summary && (
            <p className="text-indigo-400 text-lg">{proof.summary}</p>
          )}
        </div>

        {/* Content */}
        <div className="bg-slate-800/50 rounded-2xl p-6 sm:p-8 border border-slate-700/50">
          <article className="prose pamber-invert pamber-slate max-w-none pamber-headings:text-indigo-500 pamber-p:text-slate-400 pamber-li:text-slate-400 pamber-strong:text-indigo-500 pamber-a:text-indigo-400">
            <ReactMarkdown>{proof?.body_md || ""}</ReactMarkdown>
          </article>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-sm text-slate-400">
            Powered by <Link to="/" className="text-indigo-400 hover:text-indigo-400">ClientKit</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
